import { useEffect, useRef, useState } from "react";
import { FaChevronDown, FaTimes } from "react-icons/fa"; // Import Font Awesome chevron and clear icons

const SelectHasMoreMultipleInputs = ({
	id,
	placeholder,
	value = [],
	onChange,
	options,
	label,
	search,
	marginTop = "mt-20",
	displayField = "name",
	renderOption,
	disabled,
	fetchMoreData = null,
	hasMore = false,
	loader = false,
	selectedItems = [],
	attr,
	classDropdown = "absolute z-50",
}) => {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const dropdownRef = useRef(null);
	const selectedValues = Array.isArray(value) ? value : [];

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);
	const selectedOptions = selectedValues
		.map((id) => {
			const fromOptions = options.find((option) => option.id === id);
			if (fromOptions) return fromOptions;
			const fromSelectedItems = selectedItems.find((item) => item?.id === id);
			if (fromSelectedItems && attr) {
				return { ...fromSelectedItems, [displayField]: fromSelectedItems[attr] };
			}
			return null;
		})
		.filter(Boolean);

	const selectedMissingFromOptions = selectedValues.some(
		(id) => !options.find((opt) => opt.id === id) && !selectedItems.find((item) => item?.id === id),
	);

	const mergedOptionsMap = new Map();

	selectedOptions.forEach((opt) => {
		if (opt?.id != null) {
			mergedOptionsMap.set(opt.id, opt);
		}
	});

	options
		.filter((option) =>
			(option?.[displayField] || "").toLowerCase().includes(searchTerm.toLowerCase()),
		)
		.forEach((option) => {
			if (!mergedOptionsMap.has(option.id)) {
				mergedOptionsMap.set(option.id, option);
			}
		});

	const filteredOptions = [...mergedOptionsMap.values()].sort((a, b) => {
		const aSelected = selectedValues.includes(a.id);
		const bSelected = selectedValues.includes(b.id);

		const aMatchesSearch = (a?.[displayField] || "")
			.toLowerCase()
			.includes(searchTerm.toLowerCase());
		const bMatchesSearch = (b?.[displayField] || "")
			.toLowerCase()
			.includes(searchTerm.toLowerCase());

		const getPriority = (selected, match) => (selected && match ? 3 : selected ? 2 : match ? 1 : 0);

		return getPriority(bSelected, bMatchesSearch) - getPriority(aSelected, aMatchesSearch);
	});

	const handleSelectChange = (selectedValue) => {
		let newValue = [...value];
		if (newValue.includes(selectedValue)) {
			newValue = newValue.filter((val) => val !== selectedValue);
		} else {
			newValue.push(selectedValue);
		}
		onChange(id, newValue);
		setIsDropdownOpen(true);
	};

	const handleClear = () => {
		onChange(id, []);
	};

	const handleLoadMore = (e) => {
		e.stopPropagation();
		if (fetchMoreData && hasMore) {
			fetchMoreData();
		}
	};

	return (
		<div
			ref={dropdownRef}
			className={`relative flex flex-col ${disabled ? "disabled-select" : ""}`}
		>
			<label
				htmlFor={id}
				className="mb-2 flex items-center text-left font-normal text-gray-400 text-xs"
			>
				{label}
			</label>
			<div
				className="custom-scrollbar flex w-full cursor-pointer items-center justify-between rounded-lg border border-gray-600 bg-blue-gray bg-opacity-20 p-2.5 text-gray-400 text-xs placeholder-gray-400 focus:border-gray-500 focus:ring-gray-500"
				onClick={() => setIsDropdownOpen(!isDropdownOpen)}
			>
				<div className="flex w-full items-center">
					<span
						className="flex-1 truncate text-left text-xs"
						title={selectedValues.length > 0 ? selectedItems.map((e) => e?.[attr]).join(", ") : ""}
					>
						{selectedValues.length > 0 ? (
							selectedOptions.length > 0 ? (
								<>
									{selectedOptions.map((opt) => opt[displayField]).join(", ")}
									{selectedMissingFromOptions && (
										<span className="text-sm text-yellow-400 italic">
											{" "}
											— Some selections may not be visible — hover to view all
											{/* Hover to view all selected items */}
										</span>
									)}
								</>
							) : (
								<span className="text-sm text-yellow-400 italic">
									{" "}
									— Some selections may not be visible — hover to view all
									{/* Hover to view all selected items */}{" "}
								</span>
							)
						) : (
							placeholder
						)}
					</span>
					{value.length > 0 && (
						<FaTimes
							className="ml-2 cursor-pointer text-gray-400"
							onClick={(e) => {
								e.stopPropagation();
								handleClear();
							}}
						/>
					)}
					<FaChevronDown
						className={`ml-2 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
					/>
				</div>
			</div>
			{isDropdownOpen && (
				<div
					className={`${classDropdown} w-full ${marginTop} custom-scrollbar max-h-60 overflow-y-auto rounded-lg border border-gray-600 bg-navbar-bg`}
				>
					<input
						type="text"
						value={searchTerm}
						onChange={(e) => {
							setSearchTerm(e.target.value);
							search(id, e.target.value);
						}}
						placeholder={`Search ${label ? label : ""}`}
						className="w-full border-gray-600 border-b bg-navbar-bg p-2.5 text-gray-400 text-xs placeholder-gray-400 focus:border-blue-gray focus:ring-blue-gray"
					/>
					{loader ? (
						<div className="p-2 text-center text-gray-400">Loading...</div>
					) : filteredOptions?.length > 0 ? (
						<>
							{filteredOptions.map((option) => (
								<div
									key={option.id}
									onClick={() => handleSelectChange(option.id)}
									className={`cursor-pointer truncate p-2 text-left text-sm text-white hover:bg-blue-gray hover:bg-opacity-40 ${
										value?.includes(option.id) ? "bg-blue-gray bg-opacity-80" : ""
									}`}
								>
									{/* Use custom renderOption if provided, otherwise fallback to displayField */}
									{renderOption ? renderOption(option) : option[displayField]}
								</div>
							))}
							{hasMore && (
								<div
									onClick={handleLoadMore}
									className="cursor-pointer p-2 text-center text-blue-gray text-sm hover:text-white"
								>
									See More
								</div>
							)}
						</>
					) : (
						<div className="p-2 text-gray-400">No options found</div>
					)}
				</div>
			)}
		</div>
	);
};

export default SelectHasMoreMultipleInputs;
